import { ADMIN_REFUND_POLICIES, type AdminRefundPolicy } from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useEffect, type ReactElement } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Force-cancel confirmation dialog (AUT-123). Collects a required reason
 * (max 500, mirrors backend `ForceCancelOrderSchema`) + an EXPLICIT refund
 * policy from `ADMIN_REFUND_POLICIES` (NOT hand-typed) via RHF + Zod, then
 * resolves both to the parent, which routes the POST through the TOTP
 * step-up. Destructive variant (red confirm).
 *
 * The `reason` is forwarded to the audit row + order timeline server-side
 * — it is NOT PII and is required for forensics.
 */
const MAX_REASON = 500;

const schema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'orders.forceCancelDialog.reasonRequired')
    .max(MAX_REASON, 'orders.forceCancelDialog.reasonMax'),
  refundPolicy: z.enum(ADMIN_REFUND_POLICIES),
});

type ForceCancelForm = z.infer<typeof schema>;

export interface ForceCancelDialogProps {
  open: boolean;
  loading?: boolean;
  onConfirm: (input: { reason: string; refundPolicy: AdminRefundPolicy }) => void;
  onCancel: () => void;
}

/** Refund-policy select bound to the RHF form (extracted to keep the
 * dialog body under the function-length cap). Options come from
 * `ADMIN_REFUND_POLICIES` (not hand-typed). */
const RefundPolicySelect = ({
  control,
  loading,
}: {
  control: Control<ForceCancelForm>;
  loading: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Controller
      name="refundPolicy"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          fullWidth
          size="small"
          disabled={loading}
          label={t('orders.forceCancelDialog.refundPolicyLabel')}
        >
          {ADMIN_REFUND_POLICIES.map((policy) => (
            <MenuItem key={policy} value={policy}>
              {t(`orders.forceCancelDialog.refundPolicy.${policy}`)}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
};

export const ForceCancelDialog = ({
  open,
  loading = false,
  onConfirm,
  onCancel,
}: ForceCancelDialogProps): ReactElement => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<ForceCancelForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { reason: '', refundPolicy: 'full' },
  });

  useEffect(() => {
    if (open) reset({ reason: '', refundPolicy: 'full' });
  }, [open, reset]);

  const submit = (values: ForceCancelForm): void => {
    if (loading) return;
    onConfirm({ reason: values.reason.trim(), refundPolicy: values.refundPolicy });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('orders.forceCancelDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText>{t('orders.forceCancelDialog.description')}</DialogContentText>
          <RefundPolicySelect control={control} loading={loading} />
          <TextField
            {...register('reason')}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={loading}
            label={t('orders.forceCancelDialog.reasonLabel')}
            error={Boolean(errors.reason)}
            helperText={errors.reason ? t(errors.reason.message ?? '') : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading || !isValid}
            loading={loading}
          >
            {t('orders.forceCancelDialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
